import { useMemo } from 'react'

import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useApiHost } from '@liveHelper/hooks/useApiHost'
import { paramsSelector, useStore } from '@liveHelper/models/store'
import { WS_PATH } from '@oneToOne/shared/components/chat/constants'

import { pruneNilValues } from '@core/object'
import { getGuestId } from '@core/tracking/guestId'

/**
 * @returns The WebSocket URL to connect to and whether the connection should be
 * made.
 */
export const useLiveHelperWebSocketUrl = (): {
  endpoint: string
  urlParams: Record<string, string>
  shouldConnect: boolean
} => {
  const apiHost = useApiHost()
  const params = useStore(paramsSelector)
  const chatChannelId = params.chat_channel_id
  const chatChannelConfig = useChatChannelConfig()
  const { appointment } = useAppointment()

  const urlParams = useMemo(() => {
    const guestId = getGuestId()
    return pruneNilValues({
      visitor_id: guestId,
      chat_channel_id: chatChannelId,
    })
  }, [chatChannelId])

  const endpoint = useMemo(() => {
    const host = new URL(apiHost).host
    const protocol = host.startsWith('localhost') ? 'ws' : 'wss'
    return `${protocol}://${host}${WS_PATH}`
  }, [apiHost])

  const isAppointmentActive =
    !!appointment &&
    appointment.status !== 'expired' &&
    appointment.status !== 'canceled'

  const shouldConnect =
    !!chatChannelId &&
    ((chatChannelConfig?.config?.widget_availability === 'never' &&
      params.bypass_widget_availability_hidden) ||
      isAppointmentActive ||
      chatChannelConfig?.config?.widget_availability !== 'never')

  return { shouldConnect, endpoint, urlParams }
}
